import ExplainerPanel from './ExplainerPanel';

/**
 * The three first-contact concept explainers (task 539).
 *
 * The plain-language contract renamed the platform's concepts; these panels
 * explain each one at the surface where a User first meets it. Copy is written
 * in customer vocabulary — no agents, schemas, endpoints or other internals.
 * Each panel dismisses independently and permanently (per-User preference);
 * the whole set is gated by `innovate.fe.help.explainers` inside
 * `ExplainerPanel`.
 */

export const EXPLAINER_KEYS = {
  capability: 'explainer.capability',
  schemaLabels: 'explainer.schema-labels',
  connect: 'explainer.connect',
} as const;

/** What a capability is — where the pieces of an AI Service first appear. */
export function CapabilityExplainer({ className }: { className?: string }) {
  return (
    <ExplainerPanel
      panelKey={EXPLAINER_KEYS.capability}
      testId="explainer-capability"
      title="What's a capability?"
      className={className}
    >
      <p>
        A capability is one piece of AI that does a single job well — read a ticket and tag it,
        turn notes into action items, draft a reply. Your AI Service is made of one or more of
        them, working in order.
      </p>
      <p>
        Each capability carries its own instructions. You can read them here, change them, and
        keep the history — without disturbing the rest of the service.
      </p>
    </ExplainerPanel>
  );
}

/** What the "needs from you" / "gives back" labels mean on the schema surfaces. */
export function SchemaLabelsExplainer({ className }: { className?: string }) {
  return (
    <ExplainerPanel
      panelKey={EXPLAINER_KEYS.schemaLabels}
      testId="explainer-schema-labels"
      title={'What "needs from you" and "gives back" mean'}
      className={className}
    >
      <p>
        <span className="font-semibold">What it needs from you</span> is the information you hand
        the service each time you use it — the ticket text, the meeting notes, the review.
      </p>
      <p>
        <span className="font-semibold">What it gives back</span> is the answer it returns, always
        in the same shape. That predictability is what lets a page or another system use the answer
        without anyone checking it first.
      </p>
    </ExplainerPanel>
  );
}

/** What Connect gives you — on the Connect tab. */
export function ConnectExplainer({ className }: { className?: string }) {
  return (
    <ExplainerPanel
      panelKey={EXPLAINER_KEYS.connect}
      testId="explainer-connect"
      title="What Connect gives you"
      className={className}
    >
      <p>
        Connect is where this AI Service meets the rest of your world. It holds the ready-made page
        your team can open and use, and the exact call your own applications make to reach the same
        service.
      </p>
      <p>
        Both are generated from this service as it is right now, so neither can quietly fall out of
        date as you refine it.
      </p>
    </ExplainerPanel>
  );
}
